import styled, { css, keyframes } from "styled-components";

// Medidas, raios, espaçamentos e durações de
// docs/contexto-entrega/painel-entrega-documentacao_v2.md §1.2, §5 e §8.
// Cores e fontes vêm dos tokens do projeto — os hexadecimais do documento são do protótipo.
//
// Camada 4000, e não os 220 do documento. O `Menu` do header é `position: sticky;
// z-index: 2000`, o popup mobile do TopbarIdentidadeVisual é 3001, e nenhum ancestral cria
// contexto de empilhamento (TopbarWrapper e #root são `position: relative` com z-index auto),
// então o painel competia direto na raiz e perdia. Fica abaixo de ModalLayout (6000) e de
// SelectStoreModal (60000), porque o link do rodapé abre o SelectStoreModal, que tem de
// cobrir o painel.
export const Z_INDEX_PAINEL = 4000;
const Z_INDEX_BACKDROP = 3999;

const entradaPopover = keyframes`
  from { opacity: 0; transform: scale(0.95) translateY(-8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const entradaInferior = keyframes`
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
`;

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: ${Z_INDEX_BACKDROP};
`;

export const Painel = styled.div`
  position: fixed;
  z-index: ${Z_INDEX_PAINEL};
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 380px;
  max-width: calc(100vw - 32px);
  padding: 20px;
  border-radius: 24px;
  /* O protótipo usa fundos diferentes por plataforma sem motivo aparente. Aqui é o mesmo. */
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.neutralBorder};
  box-shadow: var(--cards);
  animation: ${entradaPopover} 150ms cubic-bezier(0.4, 0, 0.2, 1);

  ${(props) =>
    props.inferior &&
    css`
      left: 0;
      right: 0;
      bottom: 0;
      top: auto;
      width: 100%;
      max-width: none;
      padding: 24px;
      border-radius: 24px 24px 0 0;
      border-left: none;
      border-right: none;
      border-bottom: none;
      /* Altura máxima com rolagem interna na lista: com cinco endereços o painel do
         protótipo ultrapassa o topo da tela. */
      max-height: 85vh;
      animation: ${entradaInferior} 500ms ease-in-out;
    `}
`;

export const Cabecalho = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

export const Titulo = styled.h2`
  margin: 0;
  font-family: "Sora", system-ui, sans-serif;
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.ink};
`;

export const BotaoFechar = styled.button`
  /* 16x16 no protótipo, muito abaixo do mínimo de toque. */
  width: 44px;
  height: 44px;
  margin: -12px -12px 0 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.neutralText};
  opacity: 0.7;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -6px;
    border-radius: 50%;
  }
`;

export const LinhaExplicativa = styled.p`
  margin: 0;
  font-size: 1.4rem;
  line-height: 1.43;
  color: ${({ theme }) => theme.colors.neutralText};
`;

export const ListaEnderecos = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  overflow-y: auto;
`;

export const ItemEndereco = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 12px;
  text-align: left;
  border-radius: 20px;
  cursor: pointer;
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1),
    border-color 150ms cubic-bezier(0.4, 0, 0.2, 1);

  background-color: ${(props) =>
    props.selecionado ? props.theme.colors.primary30 : props.theme.colors.white};
  border: 1px solid
    ${(props) =>
      props.selecionado ? props.theme.colors.primary : props.theme.colors.neutralBorder};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  /* O protótipo não tem nenhum estado de foco por teclado. */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

export const Radio = styled.span`
  width: 16px;
  height: 16px;
  margin-top: 2px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid
    ${(props) =>
      props.selecionado ? props.theme.colors.primary : props.theme.colors.neutralBorder};
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.selecionado &&
    css`
      &::after {
        content: "";
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: ${props.theme.colors.primary};
      }
    `}
`;

export const ItemTextos = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const ItemTitulo = styled.span`
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.43;
  color: ${({ theme }) => theme.colors.ink};
`;

export const ItemResumo = styled.span`
  font-size: 1.2rem;
  line-height: 1.33;
  color: ${({ theme }) => theme.colors.neutralText};
`;

export const DivisorOu = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${({ theme }) => theme.colors.neutralText};
  font-size: 1.2rem;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background-color: ${({ theme }) => theme.colors.neutralBorder};
  }
`;

export const CampoBloco = styled.div`
  display: flex;
  flex-direction: column;
`;

export const RotuloCampo = styled.label`
  margin-bottom: 6px;
  font-size: 1.2rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${({ theme }) => theme.colors.neutralText};
`;

export const LinhaCampo = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
`;

export const InputCep = styled.input`
  flex: 1;
  min-width: 0;
  height: 42px;
  padding: 10px 16px;
  border-radius: 9999px;
  font-size: 1.4rem;
  line-height: 1.43;
  color: ${({ theme }) => theme.colors.ink};
  background-color: ${({ theme }) => theme.colors.neutralBg};
  outline: none;
  transition: border-color 150ms cubic-bezier(0.4, 0, 0.2, 1);

  /* No protótipo a borda não muda no erro: só surge a mensagem abaixo. */
  border: 1px solid
    ${(props) => (props.invalido ? props.theme.colors.error : props.theme.colors.neutralBorder)};

  &:focus {
    border-color: ${(props) =>
      props.invalido ? props.theme.colors.error : props.theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.neutralText};
  }
`;

export const BotaoAplicar = styled.button`
  flex-shrink: 0;
  height: 42px;
  padding: 10px 20px;
  border: 0;
  border-radius: 9999px;
  font-size: 1.4rem;
  font-weight: 700;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.primary};
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ink};
    outline-offset: 2px;
  }
`;

export const MensagemErro = styled.span`
  margin-top: 8px;
  font-size: 1.2rem;
  line-height: 1.33;
  color: ${({ theme }) => theme.colors.error};
`;

export const RodapePainel = styled.div`
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralBorder};
`;

export const LinkFarmacia = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary};

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: 8px;
  }
`;

// Anúncio da atualização para leitores de tela, sem ocupar espaço visual.
export const RegiaoStatus = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
